import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import {
    RefreshCw, Download, Loader2, ChevronRight, ChevronDown, Megaphone,
    Search, ExternalLink, AlertTriangle,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import apiClient from "../../lib/apiClient";
import { AD_PERFORMANCE_REPORT_URL } from "../../constants/api";
import AdDiagnosticsPanel from "./AdDiagnosticsPanel";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import { StatusPill, StatusFilterSelect, isDelivering } from "./adReportKit";

const DATE_PRESETS = [
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom",     label: "Custom Range" },
];

const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt = d => d.format("DD-MM-YYYY");
    switch (preset) {
        case "today":      return { from_date: fmt(today), to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")), to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(6, "day")), to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(29, "day")), to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo)
            ? { from_date: dayjs(customFrom).format("DD-MM-YYYY"), to_date: dayjs(customTo).format("DD-MM-YYYY") }
            : {};
        default: return {};
    }
};

// Meta's ranking vocabulary. BELOW_AVERAGE arrives with the percentile appended
// (BELOW_AVERAGE_35), so matching is by prefix rather than exact value.
const rankingStyle = (value) => {
    if (!value) return "";
    if (value.startsWith("ABOVE")) return "text-emerald-600 dark:text-emerald-400";
    if (value.startsWith("BELOW")) return "text-red-600 dark:text-red-400";
    return "text-muted-foreground";
};
const rankingLabel = (value) => {
    if (!value) return "—";
    if (value.startsWith("ABOVE")) return "Above avg";
    if (value.startsWith("BELOW")) return "Below avg";
    if (value === "AVERAGE") return "Average";
    return value;
};

// ── formatters ────────────────────────────────────────────────────────────────
// A metric that could not be computed comes back as null (no clicks → no CPC).
// Rendering it as 0 would read as "free", so null always renders as a dash.

const num = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));
const money = (v, currency = "₹") =>
    v == null ? "—" : `${currency}${Number(v).toLocaleString("en-IN", {
        minimumFractionDigits: 0, maximumFractionDigits: 0,
    })}`;
const money2 = (v, currency = "₹") =>
    v == null ? "—" : `${currency}${Number(v).toLocaleString("en-IN", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);

const StatTile = ({ label, value, sub, tone = "default", hint }) => (
    <div className={cn(
        "rounded-xl border p-4 bg-card",
        tone === "spend" && "border-blue-200 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/10",
        tone === "leads" && "border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10",
        tone === "cost" && "border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-900/10",
        tone === "default" && "border-border",
    )} title={hint}>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className="text-xl font-bold text-foreground mt-1">{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
);

/** Funnel strip: Meta leads → CRM leads → demos → won, as proportional bars. */
const FunnelBar = ({ row }) => {
    const base = Math.max(row.meta_leads || 0, row.crm_leads || 0, 1);
    const steps = [
        { key: "meta_leads", label: "Meta", value: row.meta_leads || 0, cls: "bg-blue-500" },
        { key: "crm_leads",  label: "CRM",  value: row.crm_leads || 0,  cls: "bg-indigo-500" },
        // Hospital tenants: the "demo" bucket is enquiries that reached an appointment.
        { key: "demos",      label: sessionStorage.getItem("product_type") === "engage" ? "Appt" : "Demo",
          value: row.demos || 0, cls: "bg-violet-500" },
        { key: "won",        label: "Won",  value: row.won || 0,        cls: "bg-emerald-500" },
    ];
    return (
        <div className="flex items-center gap-1 min-w-[120px]">
            {steps.map(step => (
                <div key={step.key} className="flex-1" title={`${step.label}: ${step.value}`}>
                    <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className={step.cls}
                            style={{ width: `${Math.min((step.value * 100) / base, 100)}%`, height: "100%" }} />
                    </div>
                    <p className="text-[9px] text-muted-foreground text-center mt-0.5">{step.value}</p>
                </div>
            ))}
        </div>
    );
};

const MetricCells = ({ row, currency }) => (
    <>
        <td className="px-2 py-2 text-right font-semibold text-foreground whitespace-nowrap">{money(row.spend, currency)}</td>
        <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{num(row.impressions)}</td>
        <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{num(row.clicks)}</td>
        <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{pct(row.ctr)}</td>
        <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{money2(row.cpc, currency)}</td>
        <td className="px-2 py-2 text-right text-foreground font-medium whitespace-nowrap">{num(row.meta_leads)}</td>
        <td className="px-2 py-2 text-right whitespace-nowrap">
            <span className={cn("font-semibold",
                row.cpl == null ? "text-muted-foreground" : "text-amber-700 dark:text-amber-400")}>
                {money(row.cpl, currency)}
            </span>
        </td>
        <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{num(row.crm_leads)}</td>
        <td className="px-2 py-2 whitespace-nowrap"><FunnelBar row={row} /></td>
        <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{money(row.cac, currency)}</td>
        <td className="px-2 py-2 text-right whitespace-nowrap">
            <span className={cn(
                row.roas == null ? "text-muted-foreground"
                    : row.roas >= 1 ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                        : "text-red-600 dark:text-red-400 font-semibold",
            )}>
                {row.roas == null ? "—" : `${Number(row.roas).toFixed(2)}×`}
            </span>
        </td>
    </>
);

export default function AdPerformanceReport() {
    const { fetch } = useApiMethods();

    const [summary, setSummary] = useState(null);
    const [diagnostics, setDiagnostics] = useState([]);
    const [benchmarks, setBenchmarks] = useState(null);
    const [diagCaveats, setDiagCaveats] = useState([]);
    const [diagWindow, setDiagWindow] = useState(null);
    const [tree, setTree] = useState([]);
    const [filters, setFilters] = useState({ objectives: [], accounts: [] });
    const [currency, setCurrency] = useState("₹");
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const [expanded, setExpanded] = useState({});      // campaign id → bool
    const [expandedAdsets, setExpandedAdsets] = useState({});

    const [datePreset, setDatePreset] = usePersistedState("ad_perf_preset", "last_month");
    const [customFrom, setCustomFrom] = usePersistedState("ad_perf_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_perf_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_perf_account", "");
    const [objective, setObjective] = usePersistedState("ad_perf_objective", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_perf_status", "");
    const [search, setSearch] = useState("");

    const buildParams = useCallback(() => {
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const params = new URLSearchParams();
        if (from_date) params.append("from_date", from_date);
        if (to_date) params.append("to_date", to_date);
        if (accountId) params.append("account_id", accountId);
        if (objective) params.append("objective", objective);
        if (statusFilter) params.append("status", statusFilter);
        if (search.trim()) params.append("search", search.trim());
        return params;
    }, [datePreset, customFrom, customTo, accountId, objective, statusFilter, search]);

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const qs = buildParams().toString();
        fetch(qs ? `${AD_PERFORMANCE_REPORT_URL}?${qs}` : AD_PERFORMANCE_REPORT_URL)
            .then(res => {
                if (res?.status === "success") {
                    setSummary(res.summary || null);
                    setDiagnostics(Array.isArray(res.diagnostics) ? res.diagnostics : []);
                    setBenchmarks(res.benchmarks || null);
                    setDiagCaveats(Array.isArray(res.diag_caveats) ? res.diag_caveats : []);
                    setDiagWindow(res.diag_window || null);
                    setTree(Array.isArray(res.data) ? res.data : []);
                    setFilters(res.filters || { objectives: [], accounts: [] });
                    setCurrency(res.currency === "INR" ? "₹" : (res.currency || "") + " ");
                } else {
                    ToastMsg("error", res?.message || "Failed to load report");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [buildParams, datePreset, customFrom, customTo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [datePreset, customFrom, customTo, accountId, objective, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const downloadExcel = async () => {
        setExporting(true);
        try {
            const params = buildParams();
            params.append("export_type", "excel");
            const response = await apiClient.get(
                `${AD_PERFORMANCE_REPORT_URL}?${params.toString()}`, { responseType: "blob" },
            );
            const blob = new Blob([response], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `ad-performance-${dayjs().format("YYYY-MM-DD")}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            ToastMsg("error", "Export failed");
        } finally {
            setExporting(false);
        }
    };

    const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
    const toggleAdset = (id) => setExpandedAdsets(p => ({ ...p, [id]: !p[id] }));

    // Surfaced as a warning rather than silently: a large gap between what Meta
    // counted and what reached the CRM means attribution is not wired, and every
    // CRM-side column below is understated until it is.
    const attributionGap = useMemo(() => {
        if (!summary || !summary.meta_leads) return null;
        const gap = summary.meta_leads - (summary.crm_leads || 0);
        return gap > 0 ? { gap, pct: (gap * 100) / summary.meta_leads } : null;
    }, [summary]);

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Megaphone className="h-6 w-6 text-primary" />
                            Ad Performance
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Meta spend joined to what those leads did in the CRM — campaign, ad set and ad.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                            Refresh
                        </Button>
                        <Button size="sm" variant="outline" onClick={downloadExcel} disabled={exporting || !tree.length}>
                            {exporting
                                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                : <Download className="h-4 w-4 mr-1.5" />}
                            Excel
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2">
                    <select value={datePreset} onChange={e => setDatePreset(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    {datePreset === "custom" && (
                        <>
                            <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                                className="h-9 w-auto text-sm" />
                            <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                                className="h-9 w-auto text-sm" />
                        </>
                    )}
                    <select value={accountId} onChange={e => setAccountId(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="">All ad accounts</option>
                        {(filters.accounts || []).map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                    </select>
                    <select value={objective} onChange={e => setObjective(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="">All objectives</option>
                        {(filters.objectives || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    <div className="relative flex-1 min-w-[180px] max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search campaign / ad set / ad…" value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && load()}
                            className="h-9 pl-8 text-sm" />
                    </div>
                </div>

                {attributionGap && attributionGap.pct >= 20 && (
                    <div className="flex items-start gap-2 rounded-lg p-3 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            Meta counted <strong>{num(summary.meta_leads)}</strong> leads but only{" "}
                            <strong>{num(summary.crm_leads)}</strong> carry an ad id in the CRM
                            ({attributionGap.pct.toFixed(0)}% unattributed). CRM Leads, Demos, Won, CAC and
                            ROAS below only cover the attributed ones — have Zapier send{" "}
                            <code className="font-mono">campaign_id</code>,{" "}
                            <code className="font-mono">adset_id</code> and{" "}
                            <code className="font-mono">ad_id</code> on the lead webhook to close the gap.
                        </span>
                    </div>
                )}

                {/* Summary tiles */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatTile tone="spend" label="Spend" value={money(summary.spend, currency)}
                            sub={`${money(summary.avg_daily_spend, currency)}/day over ${summary.days}d`} />
                        <StatTile label="Impressions" value={num(summary.impressions)}
                            sub={`${num(summary.reach)} reached`}
                            hint={summary.reach_is_deduplicated
                                ? "Reach is de-duplicated across campaigns"
                                : "Reach is summed per campaign and may double-count people who saw several"} />
                        <StatTile label="Clicks" value={num(summary.clicks)}
                            sub={`${pct(summary.ctr)} CTR · ${money2(summary.cpc, currency)} CPC`} />
                        <StatTile tone="leads" label="Meta Leads" value={num(summary.meta_leads)}
                            sub={`${num(summary.crm_leads)} in CRM`} />
                        <StatTile tone="cost" label="Cost / Lead" value={money(summary.cpl, currency)}
                            sub={summary.crm_cpl != null ? `${money(summary.crm_cpl, currency)} on CRM leads` : "—"} />
                        <StatTile label="Won / CAC" value={num(summary.won)}
                            sub={`${money(summary.cac, currency)} per win · ${summary.demos || 0} demos`} />
                    </div>
                )}

                <AdDiagnosticsPanel
                    diagnostics={diagnostics}
                    benchmarks={benchmarks}
                    caveats={diagCaveats}
                    diagWindow={diagWindow}
                    currency={currency}
                />

                {/* Tree table */}
                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                            <tr className="text-muted-foreground">
                                <th className="px-2 py-2.5 text-left font-medium min-w-[260px]">Campaign / Ad Set / Ad</th>
                                <th className="px-2 py-2.5 text-right font-medium">Spend</th>
                                <th className="px-2 py-2.5 text-right font-medium">Impr.</th>
                                <th className="px-2 py-2.5 text-right font-medium">Clicks</th>
                                <th className="px-2 py-2.5 text-right font-medium">CTR</th>
                                <th className="px-2 py-2.5 text-right font-medium">CPC</th>
                                <th className="px-2 py-2.5 text-right font-medium">Leads</th>
                                <th className="px-2 py-2.5 text-right font-medium">CPL</th>
                                <th className="px-2 py-2.5 text-right font-medium">CRM</th>
                                <th className="px-2 py-2.5 text-left font-medium">Funnel</th>
                                <th className="px-2 py-2.5 text-right font-medium">CAC</th>
                                <th className="px-2 py-2.5 text-right font-medium">ROAS</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {loading && (
                                <tr><td colSpan={12} className="py-10 text-center text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                                </td></tr>
                            )}

                            {!loading && tree.length === 0 && (
                                <tr><td colSpan={12} className="py-10 text-center text-muted-foreground">
                                    No ad spend in this period. If an account was only just connected,
                                    run a sync from Settings → Meta Ads first.
                                </td></tr>
                            )}

                            {!loading && tree.map(campaign => (
                                <Fragment key={campaign.id}>
                                    <tr className={cn("bg-muted/20 hover:bg-accent/40 cursor-pointer font-medium",
                                        !isDelivering(campaign.effective_status) && "opacity-60")}
                                        onClick={() => toggle(campaign.id)}>
                                        <td className="px-2 py-2">
                                            <div className="flex items-center gap-1.5">
                                                {expanded[campaign.id]
                                                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                                    : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                                                <span className="text-foreground truncate max-w-[280px]" title={campaign.name}>
                                                    {campaign.name}
                                                </span>
                                                <StatusPill status={campaign.effective_status} compact />
                                                {campaign.project_name && (
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-primary/10 text-primary shrink-0">
                                                        {campaign.project_name}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <MetricCells row={campaign} currency={currency} />
                                    </tr>

                                    {expanded[campaign.id] && campaign.adsets.map(adset => (
                                        <Fragment key={adset.id}>
                                            <tr className={cn("hover:bg-accent/30 cursor-pointer",
                                                !isDelivering(adset.effective_status) && "opacity-60")}
                                                onClick={() => toggleAdset(adset.id)}>
                                                <td className="px-2 py-2 pl-7">
                                                    <div className="flex items-center gap-1.5">
                                                        {expandedAdsets[adset.id]
                                                            ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                                                            : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
                                                        <span className="text-foreground truncate max-w-[220px]" title={adset.name}>
                                                            {adset.name}
                                                        </span>
                                                        <StatusPill status={adset.effective_status} compact />
                                                    </div>
                                                </td>
                                                <MetricCells row={adset} currency={currency} />
                                            </tr>

                                            {expandedAdsets[adset.id] && adset.ads.map(ad => (
                                                <tr key={ad.id} className={cn("hover:bg-accent/20 bg-background/50",
                                                    !isDelivering(ad.effective_status) && "opacity-60")}>
                                                    <td className="px-2 py-2 pl-14">
                                                        <div className="flex items-center gap-2">
                                                            {ad.thumbnail_url && (
                                                                <img src={ad.thumbnail_url} alt=""
                                                                    className="h-7 w-7 rounded object-cover shrink-0 border border-border"
                                                                    onError={e => { e.target.style.display = "none"; }} />
                                                            )}
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-foreground truncate max-w-[170px]" title={ad.name}>
                                                                        {ad.name}
                                                                    </span>
                                                                    <StatusPill status={ad.effective_status} compact />
                                                                    {ad.permalink_url && (
                                                                        <a href={ad.permalink_url} target="_blank" rel="noreferrer"
                                                                            onClick={e => e.stopPropagation()}
                                                                            className="text-muted-foreground hover:text-primary shrink-0">
                                                                            <ExternalLink className="h-3 w-3" />
                                                                        </a>
                                                                    )}
                                                                </div>
                                                                {ad.quality_ranking && (
                                                                    <p className="text-[9px] mt-0.5">
                                                                        <span className={rankingStyle(ad.quality_ranking)}>
                                                                            Q: {rankingLabel(ad.quality_ranking)}
                                                                        </span>
                                                                        <span className="text-muted-foreground"> · </span>
                                                                        <span className={rankingStyle(ad.conversion_ranking)}>
                                                                            Conv: {rankingLabel(ad.conversion_ranking)}
                                                                        </span>
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <MetricCells row={ad} currency={currency} />
                                                </tr>
                                            ))}

                                            {expandedAdsets[adset.id] && adset.ads.length === 0 && (
                                                <tr>
                                                    <td colSpan={12} className="px-2 py-2 pl-14 text-[11px] text-muted-foreground">
                                                        No ad-level rows for this ad set in the selected period.
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!loading && tree.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                        {tree.length} campaign{tree.length === 1 ? "" : "s"} · {summary?.ads || 0} ads ·
                        Ratios are recomputed from summed daily totals, not averaged across days.
                    </p>
                )}
            </div>
        </ContainerCard>
    );
}
